import React, { useState, useEffect } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import ShadedSelect from '../components/ui/ShadedSelect';
import { formControlClass } from '../components/ui/formUiClasses';
import { scrollToTop } from '../utils/scrollUtils';
import { defaultFeaturePath, featureLabel } from '../utils/userFeatureAccess';

/** @param {string} [borderClass] replaces default border-edge-strong when provided */
function registerFieldClass(borderClass = 'border-edge-strong') {
  return formControlClass.replace('border-edge-strong', borderClass);
}

function Register({ onRegister }) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const inviteCodeParam = searchParams.get('invite') || '';
  const featureAccessCodeParam = searchParams.get('access') || '';
  const requestedReturnTo = searchParams.get('returnTo') || '';
  const safeReturnTo = requestedReturnTo.startsWith('/') && !requestedReturnTo.startsWith('//')
    ? requestedReturnTo
    : '/';
  const [invitePreview, setInvitePreview] = useState(null);
  const [featureAccessPreview, setFeatureAccessPreview] = useState(null);
  const [inviteLoading, setInviteLoading] = useState(Boolean(inviteCodeParam));
  const [featureAccessLoading, setFeatureAccessLoading] = useState(Boolean(featureAccessCodeParam));
  const [userData, setUserData] = useState({
    User_ID: '',
    UID: '',
    Email_Address: '',
    Password: '',
    ConfirmPassword: '',
    School_Name: '',
    School_District: '',
    Form: '',
    Tel: '',
    User_Role: 'Stu',
    direct_marketing: false,
    email_list: false,
    ability_6d: [1.5, 1.5, 1.5, 1.5, 1.5, 1.5]
  });
  const [privacyConsent, setPrivacyConsent] = useState(false);
  const [privacyWarning, setPrivacyWarning] = useState(false);
  const [error, setError] = useState('');
  const [fieldErrors, setFieldErrors] = useState({
    Email_Address: '',
    Password: '',
    ConfirmPassword: '',
    School_Name: '',
    School_District: '',
    Form: ''
  });
  const [usernameStatus, setUsernameStatus] = useState({
    isChecking: false,
    isAvailable: true,
    message: ''
  });
  const [emailStatus, setEmailStatus] = useState({
    isChecking: false,
    isAvailable: true,
    message: ''
  });
  const [phoneStatus, setPhoneStatus] = useState({
    isChecking: false,
    isAvailable: true,
    message: ''
  });
  const { t } = useTranslation();

  useEffect(() => {
    if (!inviteCodeParam) {
      setInvitePreview(null);
      setInviteLoading(false);
      return undefined;
    }

    let cancelled = false;
    (async () => {
      setInviteLoading(true);
      try {
        const res = await fetch(`/api/invites/${encodeURIComponent(inviteCodeParam)}/preview`);
        const data = await res.json();
        if (!res.ok) throw new Error(data.message);
        if (cancelled) return;
        setInvitePreview(data);
        setUserData((prev) => ({
          ...prev,
          User_Role: data.intended_role,
          School_Name: data.school.name,
          School_District: data.school.district
        }));
      } catch {
        if (!cancelled) setInvitePreview(null);
      } finally {
        if (!cancelled) setInviteLoading(false);
      }
    })();

    return () => { cancelled = true; };
  }, [inviteCodeParam]);

  useEffect(() => {
    if (!featureAccessCodeParam || inviteCodeParam) {
      setFeatureAccessPreview(null);
      setFeatureAccessLoading(false);
      return undefined;
    }

    let cancelled = false;
    (async () => {
      setFeatureAccessLoading(true);
      try {
        const res = await fetch(`/api/feature-invites/${encodeURIComponent(featureAccessCodeParam)}/preview`);
        const data = await res.json();
        if (!res.ok) throw new Error(data.message);
        if (cancelled) return;
        setFeatureAccessPreview(data);
        setUserData((prev) => ({ ...prev, User_Role: 'Stu' }));
      } catch {
        if (!cancelled) setFeatureAccessPreview(null);
      } finally {
        if (!cancelled) setFeatureAccessLoading(false);
      }
    })();

    return () => { cancelled = true; };
  }, [featureAccessCodeParam, inviteCodeParam]);

  // Debounce function to prevent too many API calls
  const debounce = (func, wait) => {
    let timeout;
    return (...args) => {
      clearTimeout(timeout);
      timeout = setTimeout(() => func(...args), wait);
    };
  };

  // Check username availability
  const checkUsername = async (username) => {
    if (!username || username.trim() === '') {
      setUsernameStatus({
        isChecking: false,
        isAvailable: true,
        message: ''
      });
      return;
    }

    setUsernameStatus(prev => ({ ...prev, isChecking: true }));
    try {
      const response = await fetch('/api/check-username', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ username }),
      });

      const data = await response.json();
      setUsernameStatus({
        isChecking: false,
        isAvailable: data.available,
        message: data.available ?
          username.trim() !== '' ? t('common.usernameAvailable') : ''
          : t('common.usernameTaken')
      });
    } catch (error) {
      setUsernameStatus({
        isChecking: false,
        isAvailable: false,
        message: t('common.errorCheckingUsername')
      });
    }
  };

  // Debounced version of checkUsername
  const debouncedCheckUsername = debounce(checkUsername, 500);

  const isValidEmailFormat = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());

  // Check email availability
  const checkEmail = async (email) => {
    const trimmedEmail = email.trim();
    if (!trimmedEmail) {
      setEmailStatus({
        isChecking: false,
        isAvailable: true,
        message: ''
      });
      return;
    }

    if (!isValidEmailFormat(trimmedEmail)) {
      setEmailStatus({
        isChecking: false,
        isAvailable: true,
        message: ''
      });
      return;
    }

    setEmailStatus(prev => ({ ...prev, isChecking: true }));
    try {
      const response = await fetch('/api/check-email', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email: trimmedEmail.toLowerCase() }),
      });

      const data = await response.json();
      setEmailStatus({
        isChecking: false,
        isAvailable: data.available,
        message: data.available
          ? t('common.emailAvailable')
          : t('common.emailTaken')
      });
    } catch (error) {
      setEmailStatus({
        isChecking: false,
        isAvailable: false,
        message: t('common.errorCheckingEmail')
      });
    }
  };

  const debouncedCheckEmail = debounce(checkEmail, 500);

  // Check phone availability
  const checkPhone = async (phone) => {
    if (!phone || phone.trim() === '') {
      setPhoneStatus({
        isChecking: false,
        isAvailable: true,
        message: ''
      });
      return;
    }

    setPhoneStatus(prev => ({ ...prev, isChecking: true }));
    try {
      const response = await fetch('/api/check-phone', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ phone }),
      });

      const data = await response.json();
      setPhoneStatus({
        isChecking: false,
        isAvailable: data.available,
        message: data.available
          ? t('common.phoneAvailable')
          : t('common.phoneTaken')
      });
    } catch (error) {
      setPhoneStatus({
        isChecking: false,
        isAvailable: false,
        message: t('common.errorCheckingPhone')
      });
    }
  };

  const debouncedCheckPhone = debounce(checkPhone, 500);

  // Handle username change
  const handleUsernameChange = (e) => {
    const newUsername = e.target.value;
    setUserData(prev => ({ ...prev, User_ID: newUsername }));
    debouncedCheckUsername(newUsername);
  };

  const handleEmailChange = (e) => {
    const newEmail = e.target.value;
    handleFieldChange('Email_Address', newEmail);
    debouncedCheckEmail(newEmail);
  };

  const handlePhoneChange = (e) => {
    const newPhone = e.target.value;
    setUserData(prev => ({ ...prev, Tel: newPhone }));
    debouncedCheckPhone(newPhone);
  };

  // Handle field changes and clear errors
  const handleFieldChange = (fieldName, value) => {
    const nextUserData = { ...userData, [fieldName]: value };
    setUserData(nextUserData);
    if (fieldErrors[fieldName]) {
      validateField(fieldName, nextUserData);
    }
  };

  const getFieldError = (fieldName, data = userData) => {
    switch (fieldName) {
      case 'Email_Address': {
        const email = data.Email_Address.trim();
        if (!email) return t('common.emailRequired');
        return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email) ? '' : t('common.validEmailRequired');
      }
      case 'Password':
        if (!data.Password) return t('common.passwordRequired');
        return data.Password.length >= 8 && [
          /[A-Z]/.test(data.Password),
          /[a-z]/.test(data.Password),
          /\d/.test(data.Password),
          /[@$!%*?&]/.test(data.Password)
        ].filter(Boolean).length >= 3
          ? ''
          : t('common.passwordRequirements');
      case 'ConfirmPassword':
        if (!data.ConfirmPassword) return t('common.confirmPasswordRequired');
        return data.Password === data.ConfirmPassword ? '' : t('common.passwordsDoNotMatch');
      case 'School_Name':
        return data.School_Name.trim().length <= 100 ? '' : t('common.schoolNameLength');
      case 'School_District':
        return data.School_District ? '' : t('common.schoolDistrictRequired');
      case 'Form':
        if (data.User_Role === 'Tea' || data.User_Role === 'Sta') return '';
        return data.Form ? '' : t('common.formRequired');
      default:
        return '';
    }
  };

  const validateField = (fieldName, data = userData) => {
    const message = getFieldError(fieldName, data);
    setFieldErrors(prev => ({ ...prev, [fieldName]: message }));
    return message;
  };

  const renderFieldError = (fieldName) => (
    fieldErrors[fieldName] ? (
      <p id={`${fieldName}-error`} className="mt-1 text-sm text-red-600" role="alert">
        {fieldErrors[fieldName]}
      </p>
    ) : null
  );

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setPrivacyWarning(false);

    const fieldsToValidate = [
      'Email_Address',
      'Password',
      'ConfirmPassword',
      'School_Name',
      'School_District',
      ...(userData.User_Role === 'Stu' ? ['Form'] : [])
    ];
    const nextFieldErrors = fieldsToValidate.reduce((messages, fieldName) => ({
      ...messages,
      [fieldName]: getFieldError(fieldName)
    }), {});
    const errorMessages = Object.values(nextFieldErrors).filter(Boolean);

    if (errorMessages.length > 0) {
      setFieldErrors(nextFieldErrors);
      return;
    }

    // Prevent submission if username is taken
    if (!usernameStatus.isAvailable && userData.User_ID.trim() !== '') {
      setError(t('common.chooseDifferentUsername'));
      return;
    }

    if (!emailStatus.isAvailable && userData.Email_Address.trim() !== '') {
      setError(t('common.chooseDifferentEmail'));
      return;
    }

    if (!phoneStatus.isAvailable && userData.Tel.trim() !== '') {
      setError(t('common.chooseDifferentPhone'));
      return;
    }

    // Check privacy consent
    if (!privacyConsent) {
      setPrivacyWarning(true);
      setError(t('auth.privacy.warning'));
      return;
    }

    // Format data for submission
    const formData = {
      User_ID: userData.User_ID.trim(),
      Email_Address: userData.Email_Address.trim().toLowerCase(), // Ensure email is lowercase
      Password: userData.Password,
      School_Name: userData.School_Name.trim(),
      School_District: userData.School_District,
      Form: userData.User_Role === 'Stu'
        ? (userData.Form === 'other' ? 'other' : parseInt(userData.Form, 10))
        : 'other',
      Tel: userData.Tel.trim(),
      User_Role: userData.User_Role,
      invite_code: invitePreview?.code || inviteCodeParam || undefined,
      feature_invite_code: featureAccessPreview?.code || featureAccessCodeParam || undefined,
      direct_marketing: false,
      email_list: false
    };

    try {
      const success = await onRegister(formData);
      if (success) {
        // Register form is long; reset scroll before landing on Welcome so the
        // viewport does not stay pinned to the old form's bottom position.
        scrollToTop({ behavior: 'instant' });
        if (featureAccessPreview || featureAccessCodeParam) {
          navigate(defaultFeaturePath({
            access_mode: 'feature_trial',
            feature_access: (featureAccessPreview?.features || []).map((feature) => ({ feature }))
          }), { replace: true, state: { resetScroll: true } });
        } else {
          navigate(safeReturnTo, { replace: true, state: { resetScroll: true } });
        }
      } else {
        setError(t('common.registrationFailed'));
      }
    } catch (error) {
      console.error('Registration error:', error);

      // Handle specific error types with translated messages
      let errorMessage = t('auth.register.failed');

      if (error.message) {
        if (error.message.includes('already exists') || error.message.includes('duplicate')) {
          errorMessage = t('auth.register.userExists');
        } else if (error.message.includes('invalid') || error.message.includes('validation')) {
          errorMessage = t('auth.register.invalidData');
        } else if (error.message.includes('server') || error.message.includes('500')) {
          errorMessage = t('auth.register.serverError');
        } else if (error.message.includes('network') || error.message.includes('fetch')) {
          errorMessage = t('auth.register.networkError');
        } else {
          // Keep original server message if it's specific and meaningful
          errorMessage = error.message;
        }
      }

      setError(errorMessage);
    }
  };

  const handleRoleChange = (role) => {
    if ((role === 'Tea' || role === 'Sta') && !invitePreview) {
      return;
    }
    const nextUserData = {
      ...userData,
      User_Role: role,
      ...(role === 'Tea' || role === 'Sta' ? { Form: '' } : {})
    };
    setUserData(nextUserData);
    if ((role === 'Tea' || role === 'Sta') && fieldErrors.Form) {
      setFieldErrors(prev => ({ ...prev, Form: '' }));
    }
  };

  return (
    <div className="mx-auto w-full max-w-6xl px-4 py-8">
      <div className="mb-6 text-center">
        <h2 className="text-3xl font-extrabold text-ink">
          {t('auth.register.title')}
        </h2>
      </div>

      <form className="space-y-6" onSubmit={handleSubmit} noValidate>
        {error && (
          <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-red-600">
            {error}
          </div>
        )}

        {inviteLoading || featureAccessLoading ? (
          <div className="rounded-lg border border-blue-100 bg-accent/25 px-4 py-3 text-sm text-blue-800">
            {t('school.loadingInvite')}
          </div>
        ) : null}

        <div className="grid gap-6 lg:grid-cols-2 xl:grid-cols-[minmax(0,1.05fr)_minmax(20rem,0.95fr)]">
          <section className="rounded-lg border border-edge bg-surface p-5 shadow-sm">
            <div className="space-y-6">
              <div>
                <h2 className="text-2xl font-bold text-ink">
                  {t('common.accountInformation')}
                </h2>
              </div>

              <div>
                <span className="block text-sm font-medium text-ink-muted mb-2">
                  {t('report.role')}
                </span>
                <div className="grid grid-cols-3 overflow-hidden rounded-lg border border-edge bg-canvas p-1">
                  <button
                    type="button"
                    onClick={() => handleRoleChange('Stu')}
                    disabled={Boolean(invitePreview && invitePreview.intended_role !== 'Stu')}
                    className={`rounded-md py-2 text-sm font-medium transition-colors disabled:opacity-40 ${
                      userData.User_Role === 'Stu'
                        ? 'bg-surface text-link shadow-sm'
                        : 'text-ink-muted hover:text-ink'
                    }`}
                  >
                    {t('report.student')}
                  </button>
                  <button
                    type="button"
                    onClick={() => handleRoleChange('Tea')}
                    disabled={!invitePreview || invitePreview.intended_role !== 'Tea'}
                    className={`rounded-md py-2 text-sm font-medium transition-colors disabled:opacity-40 ${
                      userData.User_Role === 'Tea'
                        ? 'bg-surface text-link shadow-sm'
                        : 'text-ink-muted hover:text-ink'
                    }`}
                  >
                    {t('report.teacher')}
                  </button>
                  <button
                    type="button"
                    onClick={() => handleRoleChange('Sta')}
                    disabled={!invitePreview || invitePreview.intended_role !== 'Sta'}
                    className={`rounded-md py-2 text-sm font-medium transition-colors disabled:opacity-40 ${
                      userData.User_Role === 'Sta'
                        ? 'bg-surface text-link shadow-sm'
                        : 'text-ink-muted hover:text-ink'
                    }`}
                  >
                    {t('report.staff')}
                  </button>
                </div>
                {!invitePreview ? (
                  <p className="mt-2 text-xs text-ink-subtle">{t('school.staffInviteRequired')}</p>
                ) : null}
              </div>

              <div>
                <label htmlFor="User_ID" className="block text-sm font-medium text-ink-muted">
                  {t('common.username')}
                </label>
                <div className="mt-1">
                  <input
                    id="User_ID"
                    name="User_ID"
                    type="text"
                    value={userData.User_ID}
                    onChange={handleUsernameChange}
                    className={registerFieldClass(usernameStatus.message ? (usernameStatus.isAvailable ? 'border-green-300' : 'border-red-300') : 'border-edge-strong')}
                    placeholder={t('common.leaveBlankForAutoGeneration')}
                  />
                </div>
                {usernameStatus.message && (
                  <p className={`mt-1 text-sm ${usernameStatus.isAvailable ? 'text-green-600' : 'text-red-600'
                    }`}>
                    {usernameStatus.isChecking ? t('common.checkingAvailability') : usernameStatus.message}
                  </p>
                )}
              </div>

              <div>
                <label htmlFor="Email_Address" className="block text-sm font-medium text-ink-muted">
                  {t('common.email')}
                </label>
                <div className="mt-1">
                  <input
                    id="Email_Address"
                    name="Email_Address"
                    type="email"
                    value={userData.Email_Address}
                    onChange={handleEmailChange}
                    onBlur={() => validateField('Email_Address')}
                    aria-invalid={!!fieldErrors.Email_Address || (emailStatus.message && !emailStatus.isAvailable)}
                    aria-describedby={fieldErrors.Email_Address ? 'Email_Address-error' : undefined}
                    className={registerFieldClass(fieldErrors.Email_Address || (emailStatus.message && !emailStatus.isAvailable) ? 'border-red-300' : emailStatus.message && emailStatus.isAvailable ? 'border-green-300' : 'border-edge-strong')}
                  />
                </div>
                {renderFieldError('Email_Address')}
                {emailStatus.message && !fieldErrors.Email_Address && (
                  <p className={`mt-1 text-sm ${emailStatus.isAvailable ? 'text-green-600' : 'text-red-600'}`}>
                    {emailStatus.isChecking ? t('common.checkingAvailability') : emailStatus.message}
                  </p>
                )}
              </div>

              <div>
                <label htmlFor="Password" className="block text-sm font-medium text-ink-muted">
                  {t('common.password')}
                </label>
                <div className="mt-1">
                  <input
                    id="Password"
                    name="Password"
                    type="password"
                    value={userData.Password}
                    onChange={(e) => handleFieldChange('Password', e.target.value)}
                    onBlur={() => validateField('Password')}
                    aria-invalid={!!fieldErrors.Password}
                    aria-describedby={fieldErrors.Password ? 'Password-error' : undefined}
                    className={registerFieldClass(fieldErrors.Password ? 'border-red-300' : 'border-edge-strong')}
                    placeholder={t('common.enterPassword')}
                  />
                </div>
                {renderFieldError('Password')}
              </div>

              <div>
                <label htmlFor="ConfirmPassword" className="block text-sm font-medium text-ink-muted">
                  {t('common.confirmPassword')}
                </label>
                <div className="mt-1">
                  <input
                    id="ConfirmPassword"
                    name="ConfirmPassword"
                    type="password"
                    value={userData.ConfirmPassword}
                    onChange={(e) => handleFieldChange('ConfirmPassword', e.target.value)}
                    onBlur={() => validateField('ConfirmPassword')}
                    aria-invalid={!!fieldErrors.ConfirmPassword}
                    aria-describedby={fieldErrors.ConfirmPassword ? 'ConfirmPassword-error' : undefined}
                    className={registerFieldClass(fieldErrors.ConfirmPassword ? 'border-red-300' : 'border-edge-strong')}
                    placeholder={t('common.confirmYourPassword')}
                  />
                </div>
                {renderFieldError('ConfirmPassword')}
              </div>

              <div>
                <label htmlFor="Tel" className="block text-sm font-medium text-ink-muted">
                  {t('common.phone')}
                </label>
                <div className="mt-1">
                  <input
                    id="Tel"
                    name="Tel"
                    type="text"
                    value={userData.Tel}
                    onChange={handlePhoneChange}
                    className={registerFieldClass(phoneStatus.message ? (phoneStatus.isAvailable ? 'border-green-300' : 'border-red-300') : 'border-edge-strong')}
                    placeholder={t('common.optional')}
                  />
                </div>
                {phoneStatus.message && (
                  <p className={`mt-1 text-sm ${phoneStatus.isAvailable ? 'text-green-600' : 'text-red-600'}`}>
                    {phoneStatus.isChecking ? t('common.checkingAvailability') : phoneStatus.message}
                  </p>
                )}
              </div>
            </div>
          </section>

          <section className="rounded-lg border border-edge bg-surface p-5 shadow-sm lg:sticky lg:top-4 lg:self-start">
            <div className="space-y-6">
              <div>
                <h2 className="text-2xl font-bold text-ink">
                  {t('common.schoolInformation')}
                </h2>
              </div>

              {invitePreview ? (
                <div className="rounded-lg border border-[#29496a]/20 bg-[#29496a]/5 px-4 py-4">
                  <p className="text-xs font-semibold uppercase tracking-wide text-[#29496a]">{t('school.inviteBadge')}</p>
                  <p className="mt-1 text-lg font-semibold text-ink">{invitePreview.school.name}</p>
                  <p className="text-sm text-ink-muted font-mono">{invitePreview.code}</p>
                </div>
              ) : null}

              {featureAccessPreview ? (
                <div className="rounded-lg border border-link/30 bg-accent/25 px-4 py-4">
                  <p className="text-xs font-semibold uppercase tracking-wide text-blue-800">{t('featureAccess.inviteBadge')}</p>
                  {featureAccessPreview.label ? (
                    <p className="mt-1 text-lg font-semibold text-ink">{featureAccessPreview.label}</p>
                  ) : null}
                  <ul className="mt-2 space-y-1">
                    {(featureAccessPreview.features || []).map((key) => (
                      <li key={key} className="text-sm text-blue-900">{featureLabel(t, key)}</li>
                    ))}
                  </ul>
                  <p className="mt-2 text-sm text-ink-muted font-mono">{featureAccessPreview.code}</p>
                </div>
              ) : null}

              {userData.User_Role === 'Stu' && (
                <div>
                  <label htmlFor="Form" className="block text-sm font-medium text-ink-muted">
                    {t('common.form')}
                  </label>
                  <div className="mt-1">
                    <ShadedSelect
                      id="Form"
                      name="Form"
                      required
                      value={userData.Form}
                      onChange={(v) => handleFieldChange('Form', v)}
                      onBlur={() => validateField('Form')}
                      error={!!fieldErrors.Form}
                      className="block w-full"
                      options={[
                        { value: '', label: t('common.selectForm') },
                        { value: '1', label: 'Form 1' },
                        { value: '2', label: 'Form 2' },
                        { value: '3', label: 'Form 3' },
                        { value: '4', label: 'Form 4' },
                        { value: '5', label: 'Form 5' },
                        { value: '6', label: 'Form 6' },
                        { value: 'other', label: t('common.other') },
                      ]}
                    />
                  </div>
                  {renderFieldError('Form')}
                </div>
              )}

              <div>
                <label htmlFor="School_District" className="block text-sm font-medium text-ink-muted">
                  {t('common.schoolDistrict')}
                </label>
                <div className="mt-1">
                  <ShadedSelect
                    id="School_District"
                    name="School_District"
                    required
                    disabled={Boolean(invitePreview)}
                    value={userData.School_District}
                    onChange={(v) => handleFieldChange('School_District', v)}
                    onBlur={() => validateField('School_District')}
                    error={!!fieldErrors.School_District}
                    className="block w-full"
                    options={[
                      { value: '', label: t('common.selectSchoolDistrict') },
                      { value: 'centralAndWestern', label: t('common.districts.centralAndWestern') },
                      { value: 'eastern', label: t('common.districts.eastern') },
                      { value: 'southern', label: t('common.districts.southern') },
                      { value: 'wanChai', label: t('common.districts.wanChai') },
                      { value: 'kowloonCity', label: t('common.districts.kowloonCity') },
                      { value: 'wongTaiSin', label: t('common.districts.wongTaiSin') },
                      { value: 'yauTsimMong', label: t('common.districts.yauTsimMong') },
                      { value: 'shamShuiPo', label: t('common.districts.shamShuiPo') },
                      { value: 'islands', label: t('common.districts.islands') },
                      { value: 'kwaiTsing', label: t('common.districts.kwaiTsing') },
                      { value: 'north', label: t('common.districts.north') },
                      { value: 'saiKung', label: t('common.districts.saiKung') },
                      { value: 'shaTin', label: t('common.districts.shaTin') },
                      { value: 'taiPo', label: t('common.districts.taiPo') },
                      { value: 'tsuenWan', label: t('common.districts.tsuenWan') },
                      { value: 'tuenMun', label: t('common.districts.tuenMun') },
                      { value: 'yuenLong', label: t('common.districts.yuenLong') },
                      { value: 'kwunTong', label: t('common.districts.kwunTong') },
                      { value: 'other', label: t('common.districts.other') },
                    ]}
                  />
                </div>
                {renderFieldError('School_District')}
              </div>

              <div>
                <label htmlFor="School_Name" className="block text-sm font-medium text-ink-muted">
                  {t('report.school')}
                </label>
                <div className="mt-1">
                  <input
                    id="School_Name"
                    name="School_Name"
                    type="text"
                    value={userData.School_Name}
                    readOnly={Boolean(invitePreview)}
                    onChange={(e) => handleFieldChange('School_Name', e.target.value)}
                    onBlur={() => validateField('School_Name')}
                    aria-invalid={!!fieldErrors.School_Name}
                    aria-describedby={fieldErrors.School_Name ? 'School_Name-error' : undefined}
                    className={`${registerFieldClass(fieldErrors.School_Name ? 'border-red-300' : 'border-edge-strong')} ${invitePreview ? 'bg-canvas' : ''}`}
                    placeholder={t('common.optional')}
                  />
                </div>
                {renderFieldError('School_Name')}
              </div>
            </div>
          </section>
        </div>

        <section className="rounded-lg border border-edge bg-surface p-5 shadow-sm space-y-6">
          <div className={`p-4 rounded-md border ${privacyWarning ? 'bg-red-50 border-red-300' : 'bg-canvas border-edge'}`}>
            <h3 className="text-sm font-medium text-ink mb-2">
              {t('auth.privacy.title')}
            </h3>
            <div className="text-xs text-ink-muted space-y-2">
              <p>{t('auth.privacy.consent')}</p>
              <p>{t('auth.privacy.details')}</p>
              <p>{t('auth.privacy.rights')}</p>
              <p>{t('auth.privacy.contact')}</p>
              <p className="pt-2 border-t border-edge mt-3">
                <Link
                  to="/privacy"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-link hover:text-link-hover underline underline-offset-2 decoration-blue-600/80 hover:decoration-blue-800 font-medium transition-colors duration-150"
                >
                  {t('auth.privacy.openFullPolicy')}
                </Link>
              </p>
            </div>
            <div className="mt-4">
              <label className="flex items-start">
                <input
                  type="checkbox"
                  checked={privacyConsent}
                  onChange={(e) => {
                    setPrivacyConsent(e.target.checked);
                    if (e.target.checked) {
                      setPrivacyWarning(false);
                    }
                  }}
                  className="h-4 w-4 text-link focus:ring-ring border-edge-strong rounded mt-0.5"
                />
                <span className="text-xs text-ink-muted ml-2">
                  {t('auth.privacy.agreeToTerms')}
                </span>
              </label>
            </div>
          </div>

          <button
            type="submit"
            className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-primary-foreground bg-primary hover:bg-primary-hover focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-ring"
          >
            {t('common.register')}
          </button>

          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-edge-strong" />
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-2 bg-surface text-ink-subtle">
                {t('auth.register.hasAccount')} <Link to={requestedReturnTo ? `/login?returnTo=${encodeURIComponent(safeReturnTo)}` : '/login'} className="font-medium text-link hover:text-link-hover">{t('auth.register.signIn')}</Link>
              </span>
            </div>
          </div>
        </section>
      </form>
    </div>
  );
}

export default Register; 
